import { z } from 'zod'
import { PERMISSIONS } from './constants'
import type { FlightSyncStatus } from './flightSync'
import type { FuelState } from './fuelState'

// The flight log and the aircraft journey log (AJLB) it is written into.
//
// Pure, and shared: the entry form on a pilot's phone shows the flight
// time, block time and the aircraft total after the flight with exactly
// the code the API validates and computes them with. Tested from
// apps/api/test/domain/flightLog.test.ts.

// --- Crew -------------------------------------------------------------------

// laskukierros's four roles for the pilot logging the flight, and the two
// a second crew member can have -- the flight is billed there, so its
// shape is the site's (services/laskukierros/types.ts, FlightInput).
export const PILOT_ROLES = ['PIC', 'copilot', 'instructor', 'student'] as const
export type PilotRole = (typeof PILOT_ROLES)[number]
// 'student': a school flight's second crew member when the instructor is the pilot.
export const CREW_ROLES = ['instructor', 'copilot', 'student'] as const
export type CrewRole = (typeof CREW_ROLES)[number]

// --- Durations ----------------------------------------------------------------

// 4334 -> "72:14". The journey log writes airframe totals as hours and
// minutes, never decimal hours.
export function formatMinutes(totalMinutes: number): string {
  const sign = totalMinutes < 0 ? '-' : ''
  const abs = Math.abs(Math.round(totalMinutes))
  return `${sign}${Math.floor(abs / 60)}:${String(abs % 60).padStart(2, '0')}`
}

// "1234:56" -> 74096, "1234" -> 74040. What a member copies off the top
// of a journey log page. Null for anything else.
export function parseHoursMinutes(input: string): number | null {
  const match = /^\s*(\d{1,6})(?::([0-5]\d))?\s*$/.exec(input)
  if (!match) return null
  return Number(match[1]) * 60 + Number(match[2] ?? 0)
}

// A short duration as a pilot types it: "1:30" is 90 minutes, a bare
// "45" is 45 minutes (night and IFR time are usually under an hour). Null
// for anything else; empty is zero.
export function parseDuration(input: string): number | null {
  const trimmed = input.trim()
  if (trimmed === '') return 0
  if (/^\d{1,4}$/.test(trimmed)) return Number(trimmed)
  const match = /^(\d{1,3}):([0-5]\d)$/.exec(trimmed)
  return match ? Number(match[1]) * 60 + Number(match[2]) : null
}

// --- Digit-only entry -----------------------------------------------------------
//
// A phone's number pad has no colon, so the entry form takes digits and
// formats them as they are typed.

// Night or IFR time typed as digits, the last two being minutes: "15" is
// 0:15, "120" is 1:20. Formatted as the member types.
export function formatDurationDigits(digits: string): string {
  if (digits === '') return ''
  if (digits.length <= 2) return `0:${digits.padStart(2, '0')}`
  return `${Number(digits.slice(0, -2))}:${digits.slice(-2)}`
}

// Minutes, or null when the minutes part is 60 or more. Empty is zero.
export function durationDigitsToMinutes(digits: string): number | null {
  if (digits === '') return 0
  const minutes = Number(digits.slice(-2))
  if (minutes > 59) return null
  return Number(digits.slice(0, -2) || '0') * 60 + minutes
}

export function minutesToDurationDigits(minutes: number): string {
  if (minutes <= 0) return ''
  const hours = Math.floor(minutes / 60)
  return `${hours > 0 ? hours : ''}${String(minutes % 60).padStart(hours > 0 ? 2 : 1, '0')}`
}

// Quick time entry: the four times typed as one run of digits, HHMM each,
// in the order they happen -- off-block, takeoff, landing, on-block. Each
// group is formatted as it fills ("0937" -> "09:37"); `time` is set only
// once a group is complete and a real clock time.
export interface QuickTimeGroup {
  text: string
  time: string | null
  complete: boolean
}

export function quickTimeGroups(digits: string): QuickTimeGroup[] {
  const groups: QuickTimeGroup[] = []
  for (let i = 0; i < 4 && i * 4 < digits.length; i++) {
    const group = digits.slice(i * 4, i * 4 + 4)
    const text = group.length <= 2 ? group : `${group.slice(0, 2)}:${group.slice(2)}`
    const complete = group.length === 4
    groups.push({ text, complete, time: complete && CLOCK.test(text) ? text : null })
  }
  return groups
}

// One time typed on its own ("1000" -> "10:00"): the first quick-entry group.
export function timeFromDigits(digits: string): QuickTimeGroup {
  return quickTimeGroups(digits.slice(0, 4))[0] ?? { text: '', time: null, complete: false }
}

// What a time field shows after its `value` prop changed: null when the change
// is the field's own last report (what was typed stays as typed, even a time
// that was not real and was reported as ''), else the digits of the new value.
export function timeDigitsFromValue(value: string, lastReported: string | null): string | null {
  return value === lastReported ? null : value.replace(':', '')
}

// Applies an edit to a digits-only field whose value is displayed
// formatted. Deleting a separator the formatting added deletes the digit
// before it instead, so backspace never gets stuck on a colon.
export function nextDigits(previousDigits: string, previousText: string, nextText: string, maxDigits: number): string {
  const digits = nextText.replace(/\D/g, '')
  if (nextText.length < previousText.length && digits === previousDigits) return previousDigits.slice(0, -1)
  return digits.slice(0, maxDigits)
}

// --- Time base -----------------------------------------------------------------

// Which clock the member typed the date and times in: UTC, as aviation
// logs, or the club's own zone (the club.timezone setting).
export const TIME_BASES = ['local', 'utc'] as const
export type TimeBase = (typeof TIME_BASES)[number]

// --- Clock times -------------------------------------------------------------

const CLOCK = /^([01]\d|2[0-3]):([0-5]\d)$/

export interface FlightClockInput {
  offBlock: string
  takeoff: string
  landing: string
  onBlock: string
}

export interface ResolvedFlightClock {
  flightMinutes: number
  blockMinutes: number
  // Off-block to takeoff.
  taxiMinutes: number
  // Days after the flight's date that each time falls on: [offBlock,
  // takeoff, landing, onBlock]. 1 for a time past midnight.
  dayOffsets: [number, number, number, number]
}

const clockMinutes = (value: string): number | null => {
  const match = CLOCK.exec(value)
  return match ? Number(match[1]) * 60 + Number(match[2]) : null
}

// A flight may run past midnight once: a time that reads earlier than the
// one before it is on the next day -- 23:00 off-block, 01:20 on-block --
// provided the block, counted from the first time entered, then stays
// within this. Beyond it, a time reading earlier is a typo (landing 10:05,
// on-block 09:55 would otherwise be a block of nearly a day), and is
// flagged instead. A block that does not cross midnight is not limited
// here; the database keeps any block under 24 hours.
export const MAX_MIDNIGHT_BLOCK_MINUTES = 12 * 60

export interface FlightClockTimeline {
  // Minutes from the start of the flight's date, past 24:00 for a time on
  // the next day; null for a time blank, malformed or out of order.
  minutes: [number | null, number | null, number | null, number | null]
  dayOffsets: [number, number, number, number]
  // Which times are out of order (flightClockOrder).
  wrong: [boolean, boolean, boolean, boolean]
}

// The four times [offBlock, takeoff, landing, onBlock] laid out on the
// flight's date and the day after. Each must be the same as or later than
// the one before it -- rolling past midnight at most once, as above -- and
// landing strictly after takeoff (a flight is at least a minute in the
// air). A blank or malformed time is never flagged -- it is simply not
// entered yet -- and is skipped when comparing the ones after it.
export function flightClockTimeline(input: FlightClockInput): FlightClockTimeline {
  const raw = [input.offBlock, input.takeoff, input.landing, input.onBlock].map(clockMinutes)
  const minutes: (number | null)[] = []
  const dayOffsets: number[] = []
  const wrong: boolean[] = []
  let first: number | null = null
  let latest: number | null = null
  let day = 0
  for (let i = 0; i < raw.length; i++) {
    const value = raw[i]!
    if (value === null) {
      minutes.push(null)
      dayOffsets.push(day)
      wrong.push(false)
      continue
    }
    let at = value + day * 24 * 60
    let atDay = day
    if (latest !== null && at < latest && day === 0) {
      atDay = 1
      at += 24 * 60
    }
    const takeoff = minutes[1]
    const bad =
      (latest !== null && at < latest) ||
      (i === 2 && takeoff != null && at <= takeoff) ||
      (atDay > 0 && first !== null && at - first > MAX_MIDNIGHT_BLOCK_MINUTES)
    minutes.push(bad ? null : at)
    dayOffsets.push(bad ? day : atDay)
    wrong.push(bad)
    if (!bad) {
      first ??= at
      latest = at
      day = atDay
    }
  }
  return {
    minutes: minutes as FlightClockTimeline['minutes'],
    dayOffsets: dayOffsets as FlightClockTimeline['dayOffsets'],
    wrong: wrong as FlightClockTimeline['wrong'],
  }
}

// Which of the four times are out of order, shown in red as they are typed.
export function flightClockOrder(input: FlightClockInput): [boolean, boolean, boolean, boolean] {
  return flightClockTimeline(input).wrong
}

// The four HH:MM times of one flight, from the flight's date. Off-block may
// equal takeoff and landing may equal on-block (a member who logs to the
// minute will meet both), but takeoff to landing is at least a minute.
// Null if a time is malformed or out of order (flightClockTimeline).
export function resolveFlightClock(input: FlightClockInput): ResolvedFlightClock | null {
  const { minutes, dayOffsets, wrong } = flightClockTimeline(input)
  if (minutes.some((m) => m === null) || wrong.some(Boolean)) return null
  const [offBlock, takeoff, landing, onBlock] = minutes as [number, number, number, number]
  return {
    flightMinutes: landing - takeoff,
    blockMinutes: onBlock - offBlock,
    taxiMinutes: takeoff - offBlock,
    dayOffsets,
  }
}

// Whether a flight's airborne time and taxi-out (off-block to takeoff) look
// too long for a club flight: a typo more often than a record. Warnings
// only -- the admin sets the levels (flightLog.flightDurationWarningHours,
// flightLog.taxiWarningMinutes) and the flight still saves.
export function flightClockWarnings(
  input: FlightClockInput,
  limits: { flightDurationWarningHours: number; taxiWarningMinutes: number },
): { longFlight: boolean; longBlock: boolean; longTaxi: boolean } {
  const clock = resolveFlightClock(input)
  if (!clock) return { longFlight: false, longBlock: false, longTaxi: false }
  const limit = limits.flightDurationWarningHours * 60
  return {
    longFlight: limits.flightDurationWarningHours > 0 && clock.flightMinutes > limit,
    longBlock: limits.flightDurationWarningHours > 0 && clock.blockMinutes > limit,
    longTaxi: limits.taxiWarningMinutes > 0 && clock.taxiMinutes > limits.taxiWarningMinutes,
  }
}

// "2026-05-26" + "07:37" read as wall-clock time in `timeZone`, as an ISO
// instant -- for the admin's book start, typed in the club's time whatever
// zone the browser is in. Two passes, as the offset can differ either side
// of a DST change.
export function zonedInstant(date: string, time: string, timeZone: string): string {
  const [y, mo, d] = date.split('-').map(Number) as [number, number, number]
  const [h, mi] = time.split(':').map(Number) as [number, number]
  const naive = Date.UTC(y, mo - 1, d, h, mi)
  const offset = (instant: number) => {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).formatToParts(new Date(instant))
    const get = (type: string) => Number(parts.find((p) => p.type === type)?.value)
    return Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute')) - instant
  }
  let guess = naive
  for (let i = 0; i < 2; i++) guess = naive - offset(guess)
  return new Date(guess).toISOString()
}

// --- Journey log totals ------------------------------------------------------

export interface JourneyBook {
  bookNumber: number
  startFlightMinutes: number
  startLandings: number
  // The latest verified spread: flights after it count on from the paper's
  // figures (V930__journey_log_checkpoints.sql).
  checkpoint?: { throughOffBlockAt: string; totalMinutes: number; landings: number } | null
}

export interface JourneyFlight {
  flightId: string
  bookNumber: number
  offBlockAt: string
  flightMinutes: number
  landings: number
  totalOverrideMinutes: number | null
}

export interface JourneyTotals {
  // The airframe total before and after this flight.
  totalMinutesBefore: number
  totalMinutesAfter: number
  landingsAfter: number
  // Set when the pilot overrode the total: how far the paper book was from
  // what the electronic log would have computed (positive = paper higher).
  overrideDifferenceMinutes: number | null
}

export interface JourneySummary {
  byFlight: Map<string, JourneyTotals>
  // After the last flight -- what the next pilot starts from. Null when
  // the aircraft has no book yet.
  current: { bookNumber: number; totalMinutes: number; totalLandings: number } | null
}

// Off-block order, the flight id breaking a tie, so totals and placement
// always agree on which flight is first.
const byOffBlock = (a: { offBlockAt: string; flightId: string }, b: { offBlockAt: string; flightId: string }) =>
  Date.parse(a.offBlockAt) - Date.parse(b.offBlockAt) || (a.flightId < b.flightId ? -1 : a.flightId > b.flightId ? 1 : 0)

// The airframe totals, computed rather than stored (V540__journey_logs.sql).
//
// Each book's start is an anchor; flights count on from it in off-block
// order, and a total override on a flight is a new anchor for the flights
// after it, as is the book's latest checkpoint (the paper's carried-forward
// figures). Landings have no override and run on from the book's start or
// checkpoint.
export function journeyTotals(books: readonly JourneyBook[], flights: readonly JourneyFlight[]): JourneySummary {
  const byFlight = new Map<string, JourneyTotals>()
  const sortedBooks = [...books].sort((a, b) => a.bookNumber - b.bookNumber)
  let current: JourneySummary['current'] = null

  for (const book of sortedBooks) {
    let minutes = book.startFlightMinutes
    let landings = book.startLandings
    const checkpoint = book.checkpoint ?? null
    let anchored = checkpoint === null
    const anchor = () => {
      if (anchored || !checkpoint) return
      anchored = true
      minutes = checkpoint.totalMinutes
      landings = checkpoint.landings
    }
    const inBook = flights
      .filter((f) => f.bookNumber === book.bookNumber)
      .sort(byOffBlock)

    for (const flight of inBook) {
      if (checkpoint && Date.parse(flight.offBlockAt) > Date.parse(checkpoint.throughOffBlockAt)) anchor()
      const computed = minutes + flight.flightMinutes
      const after = flight.totalOverrideMinutes ?? computed
      landings += flight.landings
      byFlight.set(flight.flightId, {
        totalMinutesBefore: minutes,
        totalMinutesAfter: after,
        landingsAfter: landings,
        overrideDifferenceMinutes: flight.totalOverrideMinutes === null ? null : after - computed,
      })
      minutes = after
    }
    anchor()
    current = { bookNumber: book.bookNumber, totalMinutes: minutes, totalLandings: landings }
  }
  return { byFlight, current }
}

// --- Journey log placement ---------------------------------------------------

// A row of the paper book that is not a flight (V1120__journey_log_entries.sql):
// an engineer's entry taking `rowCount` rows, or a run of `rowCount` rows
// left blank. It sits in the same sequence as the flights, by `occurredAt`,
// then `sequence`; a flight counts as sequence 0.
export const JOURNEY_ENTRY_KINDS = ['entry', 'blank'] as const
export type JourneyEntryKind = (typeof JOURNEY_ENTRY_KINDS)[number]

export interface JourneyEntryRow {
  entryId: number
  bookNumber: number
  kind: JourneyEntryKind
  rowCount: number
  occurredAt: string
  sequence: number
}

// The key an entry sorts by when everything else ties: numeric, so zero-padded.
export const entryOrderKey = (entryId: number) => String(entryId).padStart(12, '0')

interface BookOrder {
  occurredAt: string
  sequence: number
  key: string
}

// The book's order: time, then sequence, then id -- one comparator for
// placement, spreads and checkpoint coverage, so they always agree on which
// row is first. Flights alone sort as byOffBlock does (sequence 0 for all).
const byBookOrder = (a: BookOrder, b: BookOrder) =>
  Date.parse(a.occurredAt) - Date.parse(b.occurredAt) ||
  a.sequence - b.sequence ||
  (a.key < b.key ? -1 : a.key > b.key ? 1 : 0)

// Where a physical book's rows are (V920__journey_logs_layout.sql). A "page"
// is a spread: two printed pages carry the same rows, and `startPage` and
// the page placement returns are the LEFT one.
export interface JourneyLayout {
  startPage: number
  startRow: number
  rowsPerSpread: number
  lastPage: number
}

// The printed pages a spread covers.
export const PAGES_PER_SPREAD = 2

export interface JourneyPlacementBook {
  bookNumber: number
  layout: JourneyLayout | null
  // The latest verified spread (its left page) and the time of the last row
  // on it. Rows after it are placed from the next spread.
  checkpoint?: { page: number; throughOffBlockAt: string } | null
}

/** The layout the open end of a book is placed by: the next spread after the checkpoint. */
export function openLayout(layout: JourneyLayout, checkpoint: { page: number } | null | undefined): JourneyLayout {
  if (!checkpoint) return layout
  return { ...layout, startPage: checkpoint.page + PAGES_PER_SPREAD, startRow: 1 }
}

/** Whether `page` is the left page of a spread of this book. */
export function isSpreadPage(layout: JourneyLayout, page: number): boolean {
  return page >= layout.startPage && (page - layout.startPage) % PAGES_PER_SPREAD === 0 && page + 1 <= layout.lastPage
}

export interface JourneyPlacement {
  // The spread's left printed page, and the row on it (from 1). For a row
  // that takes several, the first.
  page: number
  row: number
  // Past the book's last page: the row is kept, the paper book is full.
  pastLastPage: boolean
  // On a spread already checked against the paper (at or before the
  // book's latest checkpoint).
  verified: boolean
}

export interface JourneyNext extends JourneyPlacement {
  bookNumber: number
  // Rows left in the book; 0 once it is full.
  rowsRemaining: number
}

/** Rows a book holds from its first free row to its last page. */
export function bookRowCapacity(layout: JourneyLayout): number {
  const spreads = Math.floor((layout.lastPage - layout.startPage + 1) / PAGES_PER_SPREAD)
  return Math.max(0, spreads * layout.rowsPerSpread - (layout.startRow - 1))
}

function placeSlot(layout: JourneyLayout, index: number, verified = false, rowCount = 1): JourneyPlacement {
  const slot = layout.startRow - 1 + index
  const page = layout.startPage + PAGES_PER_SPREAD * Math.floor(slot / layout.rowsPerSpread)
  return {
    page,
    row: (slot % layout.rowsPerSpread) + 1,
    pastLastPage: index + rowCount - 1 >= bookRowCapacity(layout),
    verified,
  }
}

type PlacedItem = BookOrder & { rowCount: number; flightId?: string; entryId?: number }

// The paper book's page and row of every flight and entry, computed like
// the totals (never stored): flights, entries and blank runs in a book, in
// book order, fill consecutive rows from the book's first free row -- a
// flight or entry takes one row (an entry as many as it has), a blank run
// its `rowCount` -- so deleting or correcting one re-flows the rest. A book
// with no layout places nothing. `next` is where the next flight of the
// newest book goes.
export function journeyPlacement(
  books: readonly JourneyPlacementBook[],
  flights: readonly Pick<JourneyFlight, 'flightId' | 'bookNumber' | 'offBlockAt'>[],
  entries: readonly JourneyEntryRow[] = [],
): { byFlight: Map<string, JourneyPlacement>; byEntry: Map<number, JourneyPlacement>; next: JourneyNext | null } {
  const byFlight = new Map<string, JourneyPlacement>()
  const byEntry = new Map<number, JourneyPlacement>()
  let next: JourneyNext | null = null

  for (const book of [...books].sort((a, b) => a.bookNumber - b.bookNumber)) {
    const { layout } = book
    if (!layout) {
      next = null
      continue
    }
    const inBook: PlacedItem[] = [
      ...flights
        .filter((f) => f.bookNumber === book.bookNumber)
        .map((f) => ({ occurredAt: f.offBlockAt, sequence: 0, key: f.flightId, rowCount: 1, flightId: f.flightId })),
      ...entries
        .filter((e) => e.bookNumber === book.bookNumber)
        .map((e) => ({
          occurredAt: e.occurredAt,
          sequence: e.sequence,
          key: entryOrderKey(e.entryId),
          rowCount: e.rowCount,
          entryId: e.entryId,
        })),
    ].sort(byBookOrder)
    const cp = book.checkpoint ?? null
    const isClosed = (item: PlacedItem) => cp !== null && Date.parse(item.occurredAt) <= Date.parse(cp.throughOffBlockAt)
    const closed = inBook.filter(isClosed)
    const open = inBook.filter((item) => !isClosed(item))

    const place = (items: PlacedItem[], from: JourneyLayout, verified: boolean) => {
      let used = 0
      for (const item of items) {
        const placement = placeSlot(from, used, verified, item.rowCount)
        if (item.flightId !== undefined) byFlight.set(item.flightId, placement)
        else byEntry.set(item.entryId!, placement)
        used += item.rowCount
      }
      return used
    }
    // The checked part keeps its place; the open end counts on from the
    // spread after the checkpoint.
    place(closed, layout, true)
    const from = openLayout(layout, cp)
    const used = place(open, from, false)
    next = {
      ...placeSlot(from, used),
      bookNumber: book.bookNumber,
      rowsRemaining: Math.max(0, bookRowCapacity(from) - used),
    }
  }
  return { byFlight, byEntry, next }
}

// A placed row, as spreadCoverage sees it.
export interface CoverageRow {
  kind: 'flight' | JourneyEntryKind
  // A flight's id, or entryOrderKey(entryId).
  key: string
  occurredAt: string
  sequence: number
  page: number
  row: number
  rowCount: number
}

export interface SpreadCoverage {
  // The last flight on or before the spread, null when only entries and
  // blank rows are.
  lastFlightId: string | null
  // The time of the spread's last row: the checkpoint's cut-off.
  throughOffBlockAt: string
  // The spread has all its rows, which is when the paper's carried-forward
  // footer is written.
  full: boolean
  // The last row runs on to the next spread, so this one cannot be cut off alone.
  straddles: boolean
  // A row on a later spread has the same time as the last one here, so a
  // time cut-off would take it along.
  splitsTie: boolean
}

// What a checkpoint at `page` would cover. `open` are the rows after the
// latest checkpoint with their placement. Null when no row is on or before
// that spread.
export function spreadCoverage(
  rowsPerSpread: number,
  open: readonly CoverageRow[],
  page: number,
): SpreadCoverage | null {
  const covered = open.filter((r) => r.page <= page).sort(byBookOrder)
  const last = covered.at(-1)
  if (!last || last.page !== page) return null
  const lastFlight = [...covered].reverse().find((r) => r.kind === 'flight')
  const endRow = last.row + last.rowCount - 1
  return {
    lastFlightId: lastFlight?.key ?? null,
    throughOffBlockAt: last.occurredAt,
    full: endRow >= rowsPerSpread || open.some((r) => r.page > page),
    straddles: endRow > rowsPerSpread,
    splitsTie: open.some((r) => r.page > page && Date.parse(r.occurredAt) === Date.parse(last.occurredAt)),
  }
}

export interface CheckpointComparison {
  matches: boolean
  // Positive when the paper is higher than the electronic book.
  differenceMinutes: number
  differenceLandings: number
}

export function compareCheckpoint(
  paper: { totalMinutes: number; landings: number },
  computed: { totalMinutes: number; landings: number },
): CheckpointComparison {
  const differenceMinutes = paper.totalMinutes - computed.totalMinutes
  const differenceLandings = paper.landings - computed.landings
  return { matches: differenceMinutes === 0 && differenceLandings === 0, differenceMinutes, differenceLandings }
}

// --- The paper book's spreads ------------------------------------------------

export interface SpreadFlight {
  flightId: string
  page: number | null
  row: number | null
  flightMinutes: number
  landings: number
  totalMinutesBefore: number | null
  totalMinutesAfter: number | null
  landingsAfter: number | null
}

// An entry or blank run as the spread needs it.
export interface SpreadEntry {
  kind: JourneyEntryKind
  rowCount: number
  page: number | null
  row: number | null
}

export interface SpreadTotals {
  totalMinutes: number
  landings: number
}

// One row of a spread. `part` is which of its rows an entry or blank run
// is on (0 for the first), so a long entry can be drawn across its rows.
export type SpreadRow<T, E> =
  | { kind: 'empty' }
  | { kind: 'flight'; flight: T }
  | { kind: 'entry'; entry: E; part: number }
  | { kind: 'blank'; entry: E; part: number }

// One spread of the paper book as the page shows it.
export interface JourneySpread<T, E = SpreadEntry> {
  // The left printed page; the right one is page + 1.
  page: number
  // One cell per row of the spread; `empty` is a row nothing has been logged on.
  rows: SpreadRow<T, E>[]
  broughtForward: SpreadTotals
  // The figures written at the foot ("Siirto").
  carriedForward: SpreadTotals
  // Flights only: entries and blank rows add nothing.
  flightMinutes: number
  landings: number
  // All its rows are written, so the carried-forward footer is.
  full: boolean
  // At or before the book's latest checkpoint.
  verified: boolean
}

// The book as spreads, for the electronic copy of the paper page: from the
// book's first page through the spread the next flight goes on. Totals come
// from the flights' own computed totals, so a checkpoint's paper figures are
// the brought-forward of the spread after it. An entry that runs past the
// foot of a spread carries on at the head of the next.
export function journeySpreads<T extends SpreadFlight, E extends SpreadEntry = SpreadEntry>(
  book: {
    startFlightMinutes: number
    startLandings: number
    layout: JourneyLayout | null
    checkpoint?: { page: number } | null
  },
  flights: readonly T[],
  entries: readonly E[] = [],
): JourneySpread<T, E>[] {
  const { layout } = book
  if (!layout) return []
  const perSpread = layout.rowsPerSpread
  // A row's position counted from the first row of the first spread.
  const absolute = (page: number, row: number) => ((page - layout.startPage) / PAGES_PER_SPREAD) * perSpread + (row - 1)

  const placed = flights.filter((f): f is T & { page: number; row: number } => f.page !== null && f.row !== null)
  const cells = new Map<number, SpreadRow<T, E>>()
  let lastAbs = -1
  for (const f of placed) {
    const at = absolute(f.page, f.row)
    cells.set(at, { kind: 'flight', flight: f })
    lastAbs = Math.max(lastAbs, at)
  }
  for (const e of entries) {
    if (e.page === null || e.row === null) continue
    const at = absolute(e.page, e.row)
    for (let part = 0; part < e.rowCount; part++) cells.set(at + part, { kind: e.kind, entry: e, part })
    lastAbs = Math.max(lastAbs, at + e.rowCount - 1)
  }

  // The next flight goes on the following spread once this one is full.
  let lastIndex = Math.max(0, Math.floor(lastAbs / perSpread))
  if (lastAbs >= 0 && lastAbs % perSpread === perSpread - 1 && isSpreadPage(layout, layout.startPage + PAGES_PER_SPREAD * (lastIndex + 1))) {
    lastIndex += 1
  }

  const spreads: JourneySpread<T, E>[] = []
  let running: SpreadTotals = { totalMinutes: book.startFlightMinutes, landings: book.startLandings }
  for (let index = 0; index <= lastIndex; index++) {
    const page = layout.startPage + PAGES_PER_SPREAD * index
    const rows: SpreadRow<T, E>[] = Array.from(
      { length: perSpread },
      (_, i) => cells.get(index * perSpread + i) ?? { kind: 'empty' },
    )
    const here = placed.filter((f) => f.page === page).sort((a, b) => a.row - b.row)
    const first = here[0]
    const last = here.at(-1)
    const broughtForward: SpreadTotals =
      first && first.totalMinutesBefore !== null && first.landingsAfter !== null
        ? { totalMinutes: first.totalMinutesBefore, landings: first.landingsAfter - first.landings }
        : running
    const carriedForward: SpreadTotals =
      last && last.totalMinutesAfter !== null && last.landingsAfter !== null
        ? { totalMinutes: last.totalMinutesAfter, landings: last.landingsAfter }
        : broughtForward
    const rowsOnSpread = index === 0 ? perSpread - (layout.startRow - 1) : perSpread
    spreads.push({
      page,
      rows,
      broughtForward,
      carriedForward,
      flightMinutes: here.reduce((sum, f) => sum + f.flightMinutes, 0),
      landings: here.reduce((sum, f) => sum + f.landings, 0),
      full: rows.filter((r) => r.kind !== 'empty').length >= rowsOnSpread,
      verified: book.checkpoint != null && page <= book.checkpoint.page,
    })
    running = carriedForward
  }
  return spreads
}

// --- Contracts ---------------------------------------------------------------

const IsoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected YYYY-MM-DD')
const Clock = z.string().trim().regex(CLOCK, 'Expected HH:MM')
// An ICAO code or a field's own short name, as the journey log writes it.
const Airfield = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^[A-Z0-9]{2,10}$/, 'Expected an airfield code such as EFNU')
const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .nullable()
    .optional()
    .transform((v) => (v ? v : null))

export const FlightLogInputSchema = z
  .object({
    registration: z.string().trim().toUpperCase().min(3).max(10),
    flightType: z.string().trim().min(1).max(40),
    // The date of off-block; the times are wall-clock on it, or on the
    // next day where they roll past midnight (resolveFlightClock).
    date: IsoDate,
    offBlockTime: Clock,
    takeoffTime: Clock,
    landingTime: Clock,
    onBlockTime: Clock,
    departure: Airfield,
    arrival: Airfield,
    landings: z.number().int().min(1).max(99),
    personsOnBoard: z.number().int().min(1).max(20),
    // Someone else's flight: flight.log.manage only. Omitted = the caller.
    pilotUserId: z.string().trim().min(1).optional(),
    pilotRole: z.enum(PILOT_ROLES).default('PIC'),
    crewUserId: z.string().trim().min(1).nullable().optional().transform((v) => v ?? null),
    crewRole: z.enum(CREW_ROLES).nullable().optional().transform((v) => v ?? null),
    timeBase: z.enum(TIME_BASES).default('local'),
    // Rarely flown in club aircraft; zero unless entered.
    nightMinutes: z.number().int().min(0).max(24 * 60).default(0),
    nightLandings: z.number().int().min(0).max(99).default(0),
    ifrMinutes: z.number().int().min(0).max(24 * 60).default(0),
    fuelAddedLitres: z.number().min(0).max(1000).nullable().optional().transform((v) => v ?? null),
    // The total in the tanks after refuelling.
    fuelMeasuredLitres: z.number().min(0).max(1000).nullable().optional().transform((v) => v ?? null),
    // What the gauges read after shutdown. Optional, like fuel added.
    fuelRemainingLitres: z.number().min(0).max(1000).nullable().optional().transform((v) => v ?? null),
    oilAddedLitres: z.number().min(0).max(20).nullable().optional().transform((v) => v ?? null),
    remarks: optionalText(2000),
    totalOverrideMinutes: z.number().int().min(0).nullable().optional().transform((v) => v ?? null),
    totalOverrideReason: optionalText(500),
  })
  .refine(
    (f) =>
      resolveFlightClock({
        offBlock: f.offBlockTime,
        takeoff: f.takeoffTime,
        landing: f.landingTime,
        onBlock: f.onBlockTime,
      }) !== null,
    {
      message:
        'Check the times: each must be the same as or later than the one before, and landing after takeoff; a flight past midnight must be under 12 hours',
      path: ['onBlockTime'],
    },
  )
  .refine(
    (f) => {
      const clock = resolveFlightClock({
        offBlock: f.offBlockTime,
        takeoff: f.takeoffTime,
        landing: f.landingTime,
        onBlock: f.onBlockTime,
      })
      return !clock || f.nightMinutes <= clock.flightMinutes
    },
    { message: 'Night time cannot exceed the flight time', path: ['nightMinutes'] },
  )
  .refine(
    (f) => f.fuelMeasuredLitres === null || f.fuelAddedLitres === null || f.fuelMeasuredLitres >= f.fuelAddedLitres,
    { message: 'The total in the tanks cannot be less than the fuel added', path: ['fuelMeasuredLitres'] },
  )
  .refine(
    (f) => {
      const clock = resolveFlightClock({
        offBlock: f.offBlockTime,
        takeoff: f.takeoffTime,
        landing: f.landingTime,
        onBlock: f.onBlockTime,
      })
      return !clock || f.ifrMinutes <= clock.flightMinutes
    },
    { message: 'IFR time cannot exceed the flight time', path: ['ifrMinutes'] },
  )
  .refine((f) => f.nightLandings <= f.landings, {
    message: 'Night landings cannot exceed the landings',
    path: ['nightLandings'],
  })
  .refine((f) => (f.crewUserId === null) === (f.crewRole === null), {
    message: 'A second crew member needs a role, and a role needs a crew member',
    path: ['crewRole'],
  })
  .refine((f) => f.totalOverrideMinutes === null || f.totalOverrideReason !== null, {
    message: 'Say why the total differs from the computed one',
    path: ['totalOverrideReason'],
  })
export type FlightLogInput = z.infer<typeof FlightLogInputSchema>
export type FlightLogInputDraft = z.input<typeof FlightLogInputSchema>

export const FlightLogQuerySchema = z.object({
  registration: z.string().trim().toUpperCase().min(3).max(10).optional(),
  // Only flights the caller flew, as pilot or crew.
  mine: z
    .enum(['true', 'false'])
    .optional()
    .transform((v) => v === 'true'),
  page: z.coerce.number().int().min(1).max(10_000).default(1),
})

const layoutNumber = (max: number) => z.number().int().min(1).max(max).nullable().default(null)

const JourneyLogBaseSchema = z.object({
  bookNumber: z.number().int().min(1).max(9999),
  // The book's start, as an instant. Flights before it cannot be logged
  // in it; it may not precede a flight already logged.
  startedAt: z.string().datetime({ offset: true }),
  startFlightMinutes: z.number().int().min(0).max(10_000_000),
  startLandings: z.number().int().min(0).max(10_000_000),
  notes: optionalText(500),
  // The physical book's layout (see JourneyLayout): all four or none.
  startPage: layoutNumber(9999),
  startRow: layoutNumber(100),
  rowsPerSpread: layoutNumber(100),
  lastPage: layoutNumber(9999),
})

type LayoutFields = Pick<z.infer<typeof JourneyLogBaseSchema>, 'startPage' | 'startRow' | 'rowsPerSpread' | 'lastPage'>

/** The layout of a validated book input, or null when it has none. */
export function layoutOf(input: LayoutFields): JourneyLayout | null {
  const { startPage, startRow, rowsPerSpread, lastPage } = input
  if (startPage === null || startRow === null || rowsPerSpread === null || lastPage === null) return null
  return { startPage, startRow, rowsPerSpread, lastPage }
}

function checkLayout(input: LayoutFields, ctx: z.RefinementCtx) {
  const fields = ['startPage', 'startRow', 'rowsPerSpread', 'lastPage'] as const
  const set = fields.filter((f) => input[f] !== null)
  if (set.length === 0) return
  for (const f of fields) {
    if (input[f] === null) ctx.addIssue({ code: 'custom', path: [f], message: 'Set all the layout fields or none' })
  }
  const layout = layoutOf(input)
  if (!layout) return
  if (layout.startRow > layout.rowsPerSpread) {
    ctx.addIssue({ code: 'custom', path: ['startRow'], message: 'The first free row is past the rows on a spread' })
  }
  if (layout.lastPage < layout.startPage) {
    ctx.addIssue({ code: 'custom', path: ['lastPage'], message: 'The last page is before the first page' })
  }
}

export const OpenJourneyLogSchema = JourneyLogBaseSchema.superRefine(checkLayout)
export type OpenJourneyLogInput = z.infer<typeof OpenJourneyLogSchema>

export const UpdateJourneyLogSchema = JourneyLogBaseSchema.omit({ bookNumber: true }).superRefine(checkLayout)
export type UpdateJourneyLogInput = z.infer<typeof UpdateJourneyLogSchema>

// A spread checked against the paper book: its left page and the carried-
// forward figures at its foot. `accept` confirms the paper is right when it
// differs from what the electronic book computed.
export const JourneyCheckpointInputSchema = z.object({
  page: z.number().int().min(1).max(9999),
  totalMinutes: z.number().int().min(0).max(10_000_000),
  landings: z.number().int().min(0).max(10_000_000),
  note: optionalText(500),
  accept: z.boolean().default(false),
})
export type JourneyCheckpointInput = z.infer<typeof JourneyCheckpointInputSchema>

// A row of the paper book that is not a flight (V1120__journey_log_entries.sql).
// The text is typed to match the paper; `maintenanceRecordId` only links, it
// never fills the text in. `blankBefore` / `blankAfter` add runs of blank
// rows next to the entry so the paper's exact positioning is kept.
export const JOURNEY_ENTRY_MAX_ROWS = 100
const EntryRowCount = z.number().int().min(1).max(JOURNEY_ENTRY_MAX_ROWS)
const BlankRowCount = z.number().int().min(0).max(JOURNEY_ENTRY_MAX_ROWS).default(0)

export const JourneyEntryInputSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('entry'),
    occurredAt: z.string().datetime({ offset: true }),
    rowCount: EntryRowCount.default(1),
    text: z.string().trim().min(1).max(4000),
    enteredByText: optionalText(200),
    maintenanceRecordId: z.number().int().positive().nullable().optional().transform((v) => v ?? null),
    blankBefore: BlankRowCount,
    blankAfter: BlankRowCount,
  }),
  z.object({
    kind: z.literal('blank'),
    occurredAt: z.string().datetime({ offset: true }),
    rowCount: EntryRowCount.default(1),
  }),
])
export type JourneyEntryInput = z.infer<typeof JourneyEntryInputSchema>
export type JourneyEntryInputDraft = z.input<typeof JourneyEntryInputSchema>

export const UpdateFlightTypeSchema = z.object({
  label: z.string().trim().min(1).max(80).optional(),
  // null clears a translation, falling back to `label`.
  labelSv: z.string().trim().min(1).max(80).nullable().optional(),
  labelEn: z.string().trim().min(1).max(80).nullable().optional(),
  enabled: z.boolean().optional(),
  sortOrder: z.number().int().min(0).max(10_000).optional(),
})
export type UpdateFlightTypeInput = z.infer<typeof UpdateFlightTypeSchema>

// --- Responses ---------------------------------------------------------------

export interface FlightType {
  name: string
  label: string
  labelSv: string | null
  labelEn: string | null
  enabled: boolean
  inLaskukierros: boolean
  laskukierrosId: number | null
  sortOrder: number
  syncedAt: string | null
}

export interface FlightLog {
  flightId: string
  registration: string
  aircraftType: string | null
  bookNumber: number
  pilotUserId: string
  pilotName: string
  pilotLastName: string | null
  pilotRole: PilotRole
  crewUserId: string | null
  crewName: string | null
  crewLastName: string | null
  crewRole: CrewRole | null
  personsOnBoard: number
  flightType: string
  flightTypeLabel: string
  flightTypeLabelSv: string | null
  flightTypeLabelEn: string | null
  offBlockAt: string
  takeoffAt: string
  landingAt: string
  onBlockAt: string
  flightMinutes: number
  blockMinutes: number
  departure: string
  arrival: string
  landings: number
  nightMinutes: number
  nightLandings: number
  ifrMinutes: number
  fuelAddedLitres: number | null
  fuelMeasuredLitres: number | null
  oilAddedLitres: number | null
  fuelRemainingLitres: number | null
  // Followed from the entries (fuelChain); null when the aircraft has no history to follow.
  fuelState: FuelState | null
  remarks: string | null
  totalOverrideMinutes: number | null
  totalOverrideReason: string | null
  createdAt: string
  updatedAt: string
  // Computed (journeyTotals).
  totalMinutesBefore: number | null
  totalMinutesAfter: number | null
  landingsAfter: number | null
  overrideDifferenceMinutes: number | null
  // Its place in the paper book (journeyPlacement); null while the book
  // has no layout. A spread's page is its left printed page.
  page: number | null
  row: number | null
  pastLastPage: boolean
  // On a spread already checked against the paper: only a fleet manager may change it.
  verified: boolean
  // Whether the caller may correct or delete it.
  canEdit: boolean
  // Where the flight stands in laskukierros (see flightSync.ts); null when
  // it never went through the sync outbox.
  syncStatus: FlightSyncStatus | null
  syncedAt: string | null
}

// A flight as a row of the paper book's page: what the paper writes about
// it, with its place and the airframe totals.
export type JourneyBookFlight = Pick<
  FlightLog,
  | 'flightId'
  | 'pilotName'
  | 'pilotRole'
  | 'crewName'
  | 'crewRole'
  | 'personsOnBoard'
  | 'flightType'
  | 'flightTypeLabel'
  | 'flightTypeLabelSv'
  | 'flightTypeLabelEn'
  | 'offBlockAt'
  | 'takeoffAt'
  | 'landingAt'
  | 'onBlockAt'
  | 'flightMinutes'
  | 'departure'
  | 'arrival'
  | 'landings'
  | 'fuelAddedLitres'
  | 'oilAddedLitres'
  | 'remarks'
  | 'totalMinutesBefore'
  | 'totalMinutesAfter'
  | 'landingsAfter'
  | 'overrideDifferenceMinutes'
  | 'page'
  | 'row'
  | 'pastLastPage'
  | 'verified'
>

// An engineer's entry or a blank run, as a row of the paper book.
export interface JourneyBookEntry {
  entryId: number
  kind: JourneyEntryKind
  rowCount: number
  occurredAt: string
  sequence: number
  text: string | null
  enteredByText: string | null
  maintenanceRecordId: number | null
  page: number | null
  row: number | null
  pastLastPage: boolean
  verified: boolean
  // Whether the caller may change or delete it.
  canEdit: boolean
}

// GET /api/flight-logs/book/:registration/:bookNumber
export interface JourneyBookView {
  book: JourneyLogBook
  flights: JourneyBookFlight[]
  entries: JourneyBookEntry[]
  // Whether the caller may add entries (flight.maintenance.write or flight.log.manage).
  canAddEntries: boolean
}

/** Who may write entries into the journey log book: the engineer, or the fleet manager. */
export const JOURNEY_ENTRY_PERMISSIONS = [PERMISSIONS.maintenanceWrite, PERMISSIONS.flightLogManage] as const

export interface FlightLogAuditEntry {
  auditId: number
  occurredAt: string
  actorLabel: string | null
  action: string
  detail: unknown
}

export interface JourneyCheckpoint {
  registration: string
  bookNumber: number
  // The left printed page of the verified spread.
  page: number
  totalMinutes: number
  landings: number
  computedTotalMinutes: number
  computedLandings: number
  throughOffBlockAt: string
  note: string | null
  checkedBy: string | null
  checkedAt: string
}

export interface JourneyLogBook {
  registration: string
  bookNumber: number
  startedAt: string
  startFlightMinutes: number
  startLandings: number
  notes: string | null
  flightCount: number
  // The physical book's layout; null until a fleet manager enters it.
  layout: JourneyLayout | null
  rowsRemaining: number | null
  // The latest spread checked against the paper; null when none has been.
  checkpoint: JourneyCheckpoint | null
}

// One aircraft as the entry form and the admin journey log page see it.
export interface FlightLogAircraft {
  registration: string
  aircraftType: string | null
  seats: number | null
  // Usable fuel from the aircraft's performance data, for the fuel
  // remaining slider; null where none has been entered.
  usableFuelLitres: number | null
  // What a new flight starts with (fuelChain) and the aircraft's burn, for the live estimate.
  fuelAtStartLitres: number | null
  fuelAtStartEstimated: boolean
  fuelAtStartUncertain: boolean
  fuelBurnLitresPerHour: number | null
  fuelTaxiLitresPerTenMin: number
  books: JourneyLogBook[]
  // After the last flight; null until a book is opened.
  current: { bookNumber: number; totalMinutes: number; totalLandings: number } | null
  // Where the next flight goes in the paper book; null without a layout.
  next: JourneyNext | null
  // Where the last flight ended, to prefill the next departure.
  lastArrival: string | null
  lastOnBlockAt: string | null
}

// GET /api/flight-logs/config: what the entry form needs besides the data.
export interface FlightLogConfig {
  // The "close your flight plan" reminder's number; empty when not set.
  flightPlanPhone: string
  // The departure and arrival a new flight starts with.
  homeBase: string
  // The club's zone, which "local" times are in.
  clubTimezone: string
  // Above these the form warns that the airborne time or the taxi-out
  // (off-block to takeoff) looks excessive. 0 turns a warning off.
  flightDurationWarningHours: number
  taxiWarningMinutes: number
}

// The warning levels while their settings are missing or unreadable.
export const DEFAULT_FLIGHT_DURATION_WARNING_HOURS = 6
export const DEFAULT_TAXI_WARNING_MINUTES = 15

// The club's home airfield when the flightLog.homeBase setting is missing
// or unreadable: Hyvinkää.
export const DEFAULT_HOME_BASE = 'EFHV'
export const AIRFIELD_PATTERN = /^[A-Z0-9]{2,10}$/

/** The name of a flight type in the member's language, `label` where it has no translation. */
export function localizedFlightTypeLabel(
  type: { label: string; labelSv: string | null; labelEn: string | null },
  language: string,
): string {
  const translated = language.startsWith('sv') ? type.labelSv : language.startsWith('en') ? type.labelEn : null
  return translated ?? type.label
}
